<template>
  <BasePopup ref="popup" class="change-billing" :title="title">
    <slot></slot>
    <template #buttons>
      <form>
        <v-wait class="change-billing__buttons" for="Subscribing">
          <template v-slot:waiting>
            <span
              class="change-billing__btn change-billing__btn--upgrade btns btns--large"
            >
              Processing...
              <button-spinner />
            </span>
          </template>
          <button
            v-wait:disabled="'Subscribing'"
            type="button"
            class="change-billing__btn change-billing__btn--upgrade btns btns--large"
            @click="subscribe"
          >
            <span>{{ confirmButtonText }}</span>
          </button>
          <button
            v-wait:disabled="'Subscribing'"
            type="button"
            class="change-billing__btn btns btns--white btns--large"
            @click="hide"
          >
            <span>Cancel</span>
          </button>
        </v-wait>
        <FormError :errors="validation.errors" :field="targetPlan.id" />
      </form>
    </template>
  </BasePopup>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useWait } from '@/app/composables/useWait'

import type { BillingView } from '@/models/billing'
import type { PlanView } from '@/models/billing.plans'
import { ValidationState } from '@/helpers/form'

import FormError from '@/components/ui/FormError.vue'
import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import BasePopup from '@/components/ui/BasePopup.vue'
import {
  PlanChangeFailedError,
  UserCancelledError,
} from '@/services/cordova.purchase.types'

const props = defineProps<{
  billing: BillingView
  targetPlan: PlanView
  title: string
  confirmButtonText: string
}>()

const emit = defineEmits(['subscribed'])
const wait = useWait()

const validation = ref(new ValidationState())

const popup = ref<InstanceType<typeof BasePopup> | null>(null)

function show(): void {
  popup.value?.show()
}

function hide(): void {
  popup.value?.hide()
  validation.value.reset()
}

async function subscribe(): Promise<void> {
  try {
    wait.start('Subscribing')
    await props.billing.subscribeTo(props.targetPlan)
    emit('subscribed')
    validation.value.reset()
    hide()
    wait.end('Subscribing')
  } catch (error) {
    wait.end('Subscribing')
    if (error instanceof UserCancelledError) {
      return
    }
    if (error instanceof PlanChangeFailedError) {
      validation.value.showError(props.targetPlan.id, error.message)
    } else {
      validation.value.showErrors(error)
    }
  }
}

defineExpose({
  show,
  hide,
})
</script>

<style lang="scss" scoped>
.change-billing {
  &__buttons {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px 24px;
  }

  &__btn {
    font-weight: 500;

    @media (max-width: $w-medium-s) {
      width: 100%;
    }
  }
}
</style>
